import { useCallback } from "react";
import { toast } from "sonner";
import { BOARD_FAMILIES } from "@/hooks/use-boards";
import { api, ApiError } from "@/lib/api";
import { useLiveSync } from "@/lib/query/live-sync";

export interface QuickMove {
  taskId: string;
  title: string;
  /** Where the card is now: Undo puts it back here. */
  fromColumnId: string;
  toColumnId: string;
  /** The toast's first word: "Done", "Reopened". */
  verb: string;
  /** After Undo has put the card back, e.g. to show a row hidden optimistically again. */
  onUndone?: () => void;
}

/**
 * One tap moves a card and offers Undo, which moves it back to the column it
 * came from. Both go through `localChange`, so the move costs one refetch
 * batched with its own revision event. Resolves true when the move landed.
 */
export function useQuickMove(): (move: QuickMove) => Promise<boolean> {
  const { localChange } = useLiveSync();
  return useCallback(
    async ({ taskId, title, fromColumnId, toColumnId, verb, onUndone }: QuickMove) => {
      try {
        await api.moveTask(taskId, { column: toColumnId, force: true });
      } catch (error) {
        toast.error(error instanceof ApiError ? error.message : "Could not move the card");
        return false;
      }
      localChange(BOARD_FAMILIES);
      toast.success(`${verb} · ${title}`, {
        action: {
          label: "Undo",
          onClick: () => {
            api
              .moveTask(taskId, { column: fromColumnId, force: true })
              .then(() => {
                onUndone?.();
                localChange(BOARD_FAMILIES);
              })
              .catch((error: unknown) => toast.error(error instanceof ApiError ? error.message : "Could not undo that move"));
          },
        },
      });
      return true;
    },
    [localChange],
  );
}
